import React from 'react';
import * as BizCharts from 'bizcharts';
import {
	COMPARISON_SERIES_FIELD,
	COMPARISON_SERIES_PREVIOUS,
} from '../../utils/AnalyticsComparisonUtils';

const { Chart, Axis, Geom, Legend, Tooltip } = BizCharts as any;

const AXIS_Y_LINE_COLOR = '#D9D9D9';
const AXIS_X_LINE_COLOR = '#E8E8E8';
const DEFAULT_HEIGHT = 360;

export type MetricLineChartProps = {
	data: any[];
	xField?: string;
	yField?: string;
	hasComparison: boolean;
	currentColor: string;
	previousColor: string;
	axisXTitle?: string;
	axisYTitle: string;
	formatYAxisLabel: (val: any) => string;
	/**
	 * Geom-level tooltip definition used in single (non-comparison) mode. Maps to BizCharts'
	 * `[fields, formatter]` API where the formatter returns `{ name, value }`. The `value` is
	 * rendered via `itemTpl` and may contain HTML.
	 */
	pointTooltipDefinition?: [string, (...args: any[]) => { name: string; value: string }];
	/** Custom content builder used in comparison mode (Tooltip.customContent). */
	renderComparisonTooltip?: (title: string, items: any[]) => string;
	height?: number;
};

const SINGLE_TOOLTIP_DOM_STYLES = {
	'g2-tooltip': {
		padding: '0',
		boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
		borderRadius: '4px',
	},
	'g2-tooltip-list': {
		margin: '0',
		padding: '0',
	},
} as const;

const COMPARISON_TOOLTIP_DOM_STYLES = {
	'g2-tooltip': {
		padding: '0',
		boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
		borderRadius: '4px',
		background: '#fff',
	},
	'g2-tooltip-list': {
		margin: '0',
		padding: '0',
	},
} as const;

const SINGLE_ITEM_TPL =
	'<li data-index={index} style="list-style:none;padding:0;margin:0;">{value}</li>';

const CROSSHAIRS_CONFIG = {
	type: 'x',
	line: {
		style: {
			stroke: 'rgba(85, 85, 85, 0.8)',
			lineWidth: 1,
			lineDash: [4, 4],
		},
	},
};

const MetricLineChart = ({
	data,
	xField = 'monthLabel',
	yField = 'metricValue',
	hasComparison,
	currentColor,
	previousColor,
	axisXTitle = 'Month',
	axisYTitle,
	formatYAxisLabel,
	pointTooltipDefinition,
	renderComparisonTooltip,
	height = DEFAULT_HEIGHT,
}: MetricLineChartProps) => {
	const seriesColor = (seriesValue: any) =>
		seriesValue === COMPARISON_SERIES_PREVIOUS ? previousColor : currentColor;
	const seriesLineDash = (seriesValue: any) =>
		seriesValue === COMPARISON_SERIES_PREVIOUS ? { lineDash: [4, 4] } : {};
	const seriesPointStyle = (seriesValue: any) => {
		const isPrevious = seriesValue === COMPARISON_SERIES_PREVIOUS;
		return {
			fill: isPrevious ? '#fff' : currentColor,
			stroke: isPrevious ? previousColor : currentColor,
			lineWidth: 2,
		};
	};

	return (
		<Chart
			height={height}
			data={data}
			autoFit={true}
			forceFit={true}
			theme="openwrench"
			scale={{
				[yField]: {
					min: 0,
					nice: true,
				},
			}}
		>
			<Axis
				name={xField}
				title={{
					text: axisXTitle,
					style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
					offset: 40,
				}}
				line={{ style: { stroke: AXIS_X_LINE_COLOR } }}
				tickLine={null}
				label={{
					style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
				}}
			/>
			<Axis
				name={yField}
				title={{
					text: axisYTitle,
					style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
					offset: 80,
				}}
				label={{
					formatter: formatYAxisLabel,
					style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
				}}
				line={{ style: { stroke: AXIS_Y_LINE_COLOR } }}
				tickLine={null}
				grid={{
					line: {
						style: {
							stroke: AXIS_Y_LINE_COLOR,
							lineDash: [4, 4],
						},
					},
				}}
			/>
			<Legend visible={false} />
			{hasComparison ? (
				<Tooltip
					showTitle={false}
					shared={true}
					showCrosshairs={true}
					crosshairs={CROSSHAIRS_CONFIG}
					customContent={renderComparisonTooltip}
					domStyles={COMPARISON_TOOLTIP_DOM_STYLES}
				/>
			) : (
				<Tooltip
					showTitle={false}
					showCrosshairs={true}
					crosshairs={CROSSHAIRS_CONFIG}
					itemTpl={SINGLE_ITEM_TPL}
					domStyles={SINGLE_TOOLTIP_DOM_STYLES}
				/>
			)}
			<Geom
				key={hasComparison ? 'line-cmp' : 'line-single'}
				type="line"
				position={`${xField}*${yField}`}
				color={[COMPARISON_SERIES_FIELD, seriesColor]}
				size={2}
				style={[COMPARISON_SERIES_FIELD, seriesLineDash]}
				tooltip={pointTooltipDefinition}
			/>
			<Geom
				key={hasComparison ? 'point-cmp' : 'point-single'}
				type="point"
				position={`${xField}*${yField}`}
				color={[COMPARISON_SERIES_FIELD, seriesColor]}
				size={5}
				shape={'circle'}
				style={[COMPARISON_SERIES_FIELD, seriesPointStyle]}
				tooltip={false}
			/>
		</Chart>
	);
};

export default MetricLineChart;
